import React, { useState, useCallback, useEffect } from "react";
import {
  Plus,
  Calendar,
  Package,
  Trash2,
  MoveRight,
  Users,
  Search,
  X,
} from "lucide-react";
import { OrderTableData } from "features/stock-ordering/core/domain/order-table-row.model";
import {
  BatchModel,
  BatchProduct,
} from "features/stock-ordering/core/domain/batch.model";
import { moneyFormatter } from "./stock-ordering-utils";

interface ConfirmOrderBatchProductCreatorProps {
  selectedProducts: OrderTableData[];
  onBatchesChange: (batches: BatchModel[], totalCost: number) => void;
  onConfirm: () => void;
}

// Extracted CustomQuantityInput component
const CustomQuantityInput = ({
  productId,
  batchId,
  initialQuantity,
  onQuantityChange,
}: {
  productId: string;
  batchId: string;
  initialQuantity: number;
  onQuantityChange: (
    productId: string,
    batchId: string,
    quantity: number
  ) => void;
}) => {
  const [localQuantity, setLocalQuantity] = useState(
    initialQuantity.toString()
  );

  // Update local state when prop changes (from external updates)
  useEffect(() => {
    setLocalQuantity(initialQuantity.toString());
  }, [initialQuantity]);

  const handleInputChange = (value: string) => {
    setLocalQuantity(value);
    const numValue = parseInt(value) || 0;
    onQuantityChange(productId, batchId, numValue);
  };

  const handleInputBlur = () => {
    const numValue = parseInt(localQuantity) || 0;
    setLocalQuantity(numValue.toString()); // Clean up the display
    onQuantityChange(productId, batchId, numValue);
  };

  const increment = () => {
    const newValue = (parseInt(localQuantity) || 0) + 1;
    setLocalQuantity(newValue.toString());
    onQuantityChange(productId, batchId, newValue);
  };

  const decrement = () => {
    const newValue = Math.max(0, (parseInt(localQuantity) || 0) - 1);
    setLocalQuantity(newValue.toString());
    onQuantityChange(productId, batchId, newValue);
  };

  return (
    <div className="flex items-center gap-2">
      <button
        onClick={decrement}
        className="w-6 h-6 rounded bg-gray-200 hover:bg-gray-300 flex items-center justify-center text-gray-600 text-sm"
      >
        -
      </button>
      <input
        type="number"
        value={localQuantity}
        onChange={(e) => handleInputChange(e.target.value)}
        onBlur={handleInputBlur}
        className="w-16 text-center border border-gray-300 rounded py-1 text-sm"
        min="0"
      />
      <button
        onClick={increment}
        className="w-6 h-6 rounded bg-gray-200 hover:bg-gray-300 flex items-center justify-center text-gray-600 text-sm"
      >
        +
      </button>
    </div>
  );
};

// Extracted ProductCard component
const ProductCard = ({
  product,
  onMoveClick,
  onRemoveClick,
  showMoveOptions = false,
  currentBatchId,
  inBatch = false,
  activeDropdown,
  setActiveDropdown,
  batches,
  onMoveProductToBatch,
  onUpdateProductQuantity,
}: {
  product: OrderTableData | BatchProduct;
  onMoveClick?: (productId: string) => void;
  onRemoveClick?: (productId: string) => void;
  showMoveOptions?: boolean;
  currentBatchId?: string;
  inBatch?: boolean;
  activeDropdown: string | null;
  setActiveDropdown: (id: string | null) => void;
  batches: BatchModel[];
  onMoveProductToBatch: (productId: string, batchId: string) => void;
  onUpdateProductQuantity: (
    productId: string,
    batchId: string,
    quantity: number
  ) => void;
}) => {
  const batchProduct = product as BatchProduct;
  const isInBatch = inBatch && "batchQuantity" in product;

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-3 shadow-sm hover:shadow-md transition-shadow duration-200">
      <div className="flex justify-between items-start mb-2">
        <div className="flex-1">
          <h4 className="font-medium text-gray-900 text-sm">
            {product.productName}
          </h4>
          <p className="text-xs text-gray-500">ID: {product.productId}</p>
        </div>
        <div className="flex gap-1">
          {showMoveOptions && !isInBatch && (
            <div className="relative">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setActiveDropdown(
                    activeDropdown === product.productId
                      ? null
                      : product.productId
                  );
                }}
                className="p-1 text-blue-600 hover:bg-blue-50 rounded transition-colors duration-200"
              >
                <MoveRight className="h-4 w-4" />
              </button>
              {activeDropdown === product.productId && (
                <div className="absolute right-0 top-8 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-20 min-w-32">
                  <div className="text-xs text-gray-500 px-3 py-1 border-b">
                    Add to batch:
                  </div>
                  {batches.map((batch) => (
                    <button
                      key={batch.id}
                      onClick={() => {
                        onMoveProductToBatch(product.productId, batch.id);
                        setActiveDropdown(null); // Close dropdown after selection
                      }}
                      className="w-full text-left px-3 py-1 text-xs hover:bg-gray-50 text-gray-700"
                    >
                      {batch.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          {onRemoveClick && isInBatch && (
            <button
              onClick={() => onRemoveClick(product.productId)}
              className="p-1 text-red-500 hover:bg-red-50 rounded transition-colors duration-200"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 text-xs text-gray-600 mb-2">
        <div>
          UOM: <span className="font-medium">{product.uom || "N/A"}</span>
        </div>
        <div>
          Unit Cost:{" "}
          <span className="font-medium">
            ₱{moneyFormatter(product.cost, 2)}
          </span>
        </div>
        <div>
          Multiplier:{" "}
          <span className="font-medium">
            {moneyFormatter(product.uom_multiplier)}
          </span>
        </div>
        {isInBatch && (
          <div>
            Total Units:{" "}
            <span className="font-medium">
              {moneyFormatter(batchProduct.qty_per_order)}
            </span>
          </div>
        )}
      </div>

      {isInBatch && currentBatchId && (
        <div className="mt-3 p-2 bg-gray-50 rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-gray-700">
              Batch Quantity:
            </span>
            <CustomQuantityInput
              productId={product.productId}
              batchId={currentBatchId}
              initialQuantity={batchProduct.batchQuantity}
              onQuantityChange={onUpdateProductQuantity}
            />
          </div>
          <div className="text-right">
            <span className="text-sm font-bold text-blue-600">
              ₱{moneyFormatter(batchProduct.batchTotalCost, 2)}
            </span>
          </div>
        </div>
      )}
      {/* For unassigned products, just show the unit cost */}
      {!isInBatch && (
        <div className="text-right mt-2">
          <span className="text-xs text-gray-500">Unit: </span>
          <span className="text-sm font-medium text-gray-700">
            ₱{moneyFormatter(product.cost, 2)}
          </span>
        </div>
      )}
    </div>
  );
};

export function ConfirmOrderBatchProductCreator({
  selectedProducts = [],
  onBatchesChange = () => {},
  onConfirm = () => {},
}: ConfirmOrderBatchProductCreatorProps) {
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [batches, setBatches] = useState<BatchModel[]>([]);
  const [unassignedProducts, setUnassignedProducts] = useState<
    OrderTableData[]
  >([]);

  // Search functionality
  const [searchTerm, setSearchTerm] = useState<string>("");

  // Filter products based on search term (with debugging)
  const filteredProducts = unassignedProducts.filter((product) => {
    if (!searchTerm) return true;

    const searchLower = searchTerm.toLowerCase();
    return (
      product.productName.toLowerCase().includes(searchLower) ||
      product.productId.toLowerCase().includes(searchLower) ||
      (product.uom && product.uom.toLowerCase().includes(searchLower))
    );
  });

  // Clear search
  const clearSearch = () => {
    setSearchTerm("");
  };

  // Initialize unassigned products when selectedProducts changes
  useEffect(() => {
    if (selectedProducts && selectedProducts.length > 0) {
      const cleanedProducts = selectedProducts.map((product) => ({
        ...product,
        cost:
          typeof product.cost === "string"
            ? parseFloat(product.cost)
            : product.cost,
        orderQty: 0,
        itemTotalCost: 0,
        qty_per_order: 0,
      }));
      setUnassignedProducts(cleanedProducts);
    }
  }, [selectedProducts]);

  // Notify parent when batches change
  useEffect(() => {
    onBatchesChange(batches, totalOrderValue);
  }, [batches, onBatchesChange]);

  // Also add click outside handler to close dropdown:
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      setActiveDropdown(null);
    };

    if (activeDropdown) {
      document.addEventListener("click", handleClickOutside);
      return () => document.removeEventListener("click", handleClickOutside);
    }
  }, [activeDropdown]);

  // Create new batch
  const handleCreateBatch = () => {
    const newBatch: BatchModel = {
      id: `batch-${Date.now()}`,
      name: `Batch ${batches.length + 1}`,
      deliveryDate: null,
      products: [],
      totalCost: 0,
    };
    setBatches((prev) => [...prev, newBatch]);
  };

  // Update batch delivery date
  const handleDateChange = useCallback((batchId: string, date: string) => {
    setBatches((prev) =>
      prev.map((batch) =>
        batch.id === batchId ? { ...batch, deliveryDate: date } : batch
      )
    );
  }, []);

  // Update batch name
  const handleNameChange = useCallback((batchId: string, name: string) => {
    setBatches((prev) =>
      prev.map((batch) => (batch.id === batchId ? { ...batch, name } : batch))
    );
  }, []);

  // Delete batch
  const handleDeleteBatch = useCallback((batchId: string) => {
    setBatches((prev) => {
      const batchToDelete = prev.find((b) => b.id === batchId);
      if (batchToDelete) {
        setUnassignedProducts((current) => [
          ...current,
          ...batchToDelete.products,
        ]);
      }
      return prev.filter((b) => b.id !== batchId);
    });
  }, []);

  // Move product to batch
  const handleMoveProductToBatch = useCallback(
    (productId: string, targetBatchId: string) => {
      const product = unassignedProducts.find((p) => p.productId === productId);
      if (!product) return;

      // Don't remove from unassigned - allow same product in multiple batches
      setBatches((prev) =>
        prev.map((batch) => {
          if (batch.id === targetBatchId) {
            // Check if product already exists in this batch
            const existingProduct = batch.products.find(
              (p) => p.productId === productId
            );
            if (existingProduct) {
              return batch; // Product already in this batch
            }

            // Create batch product with default quantity of 1
            const batchProduct: BatchProduct = {
              ...product,
              batchQuantity: 1,
              batchTotalCost: product.cost * 1,
              qty_per_order: 1 * product.uom_multiplier,
            };

            const newProducts = [...batch.products, batchProduct];
            return {
              ...batch,
              products: newProducts,
              totalCost: newProducts.reduce(
                (sum, p) => sum + p.batchTotalCost,
                0
              ),
            };
          }
          return batch;
        })
      );
    },
    [unassignedProducts]
  );

  // Remove product from batch
  const handleRemoveProductFromBatch = useCallback(
    (productId: string, batchId: string) => {
      setBatches((prev) =>
        prev.map((batch) => {
          if (batch.id === batchId) {
            const newProducts = batch.products.filter(
              (p) => p.productId !== productId
            );
            return {
              ...batch,
              products: newProducts,
              totalCost: newProducts.reduce(
                (sum, p) => sum + p.batchTotalCost,
                0
              ),
            };
          }
          return batch;
        })
      );
    },
    []
  );

  // Update product quantity in batch
  const handleUpdateProductQuantity = useCallback(
    (productId: string, batchId: string, newQuantity: number) => {
      setBatches((prev) =>
        prev.map((batch) => {
          if (batch.id === batchId) {
            const newProducts = batch.products.map((product) => {
              if (product.productId === productId) {
                const updatedProduct = {
                  ...product,
                  batchQuantity: Math.max(0, newQuantity),
                  batchTotalCost: product.cost * Math.max(0, newQuantity),
                  qty_per_order:
                    Math.max(0, newQuantity) * product.uom_multiplier,
                };
                return updatedProduct;
              }
              return product;
            });

            return {
              ...batch,
              products: newProducts,
              totalCost: newProducts.reduce(
                (sum, p) => sum + p.batchTotalCost,
                0
              ),
            };
          }
          return batch;
        })
      );
    },
    []
  );

  // Calculate totals - only count products with quantities > 0
  const totalOrderValue = batches.reduce(
    (sum, batch) => sum + batch.totalCost,
    0
  );

  // Updated validation - check if at least one batch has products with quantity > 0
  // Removed the validation for delivery date - THEY WANT IT! WALA AKO MAGAGAWA
  const isReadyToConfirm =
    batches.length > 0 &&
    // batches.every((batch) => batch.deliveryDate) &&
    batches.some((batch) => batch.products.some((p) => p.batchQuantity > 0));

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Staggered Order Setup
          </h1>
          <p className="text-gray-600">
            Organize your products into delivery batches with specific dates
          </p>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
            <div className="flex items-center gap-2 mb-1">
              <Package className="h-4 w-4 text-blue-600" />
              <span className="text-sm font-medium text-gray-700">
                Total Products
              </span>
            </div>
            <p className="text-2xl font-bold text-gray-900">
              {selectedProducts?.length}
            </p>
            <p className="text-xs text-gray-500">Originally selected</p>
          </div>

          <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
            <div className="flex items-center gap-2 mb-1">
              <Users className="h-4 w-4 text-green-600" />
              <span className="text-sm font-medium text-gray-700">
                Active Batches
              </span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{batches.length}</p>
          </div>

          <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
            <div className="flex items-center gap-2 mb-1">
              <Package className="h-4 w-4 text-amber-600" />
              <span className="text-sm font-medium text-gray-700">
                Available Products
              </span>
            </div>
            <p className="text-2xl font-bold text-gray-900">
              {unassignedProducts.length}
            </p>
            <p className="text-xs text-gray-500">Ready to add to batches</p>
          </div>

          <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-sm font-medium text-gray-700">
                Total Value
              </span>
            </div>
            <p className="text-2xl font-bold text-blue-600">
              ₱{moneyFormatter(totalOrderValue, 2)}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Sticky Available Products */}
          <div className="lg:col-span-1">
            <div className="sticky top-32 z-0">
              <div className="bg-white rounded-xl border border-gray-200 shadow-lg">
                <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-4 py-3 border-b border-gray-200 rounded-t-xl">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                      <Package className="h-4 w-4" />
                      Available Products ({filteredProducts.length})
                    </h3>
                  </div>

                  {/* Search Input */}
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Search className="h-4 w-4 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Search products..."
                      className="w-full pl-9 pr-8 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    {searchTerm && (
                      <button
                        onClick={clearSearch}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      >
                        <X className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                      </button>
                    )}
                  </div>

                  <p className="text-xs text-gray-500 mt-2">
                    {searchTerm
                      ? `${filteredProducts.length} products found`
                      : "Add these products to batches with specific quantities"}
                  </p>
                </div>

                <div className="p-4 max-h-96 overflow-y-auto space-y-3">
                  {filteredProducts.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <Package className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      {searchTerm ? (
                        <>
                          <p>No products found</p>
                          <p className="text-xs">Try a different search term</p>
                          <button
                            onClick={clearSearch}
                            className="mt-2 text-blue-600 text-xs hover:text-blue-700"
                          >
                            Clear search
                          </button>
                        </>
                      ) : (
                        <>
                          <p>No products available</p>
                          <p className="text-xs">
                            Check your product selection
                          </p>
                        </>
                      )}
                    </div>
                  ) : (
                    filteredProducts.map((product, index) => (
                      <ProductCard
                        key={`${index}-${product.productId}`}
                        product={product}
                        showMoveOptions={batches.length > 0}
                        inBatch={false}
                        activeDropdown={activeDropdown}
                        setActiveDropdown={setActiveDropdown}
                        batches={batches}
                        onMoveProductToBatch={handleMoveProductToBatch}
                        onUpdateProductQuantity={handleUpdateProductQuantity}
                      />
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Batches */}
          <div className="lg:col-span-2">
            <div className="space-y-6">
              {/* Create Batch Button */}
              <button
                onClick={handleCreateBatch}
                className="w-full p-4 border-2 border-dashed border-gray-300 rounded-xl hover:border-blue-500 hover:bg-blue-50 transition-all duration-200 group"
              >
                <div className="flex items-center justify-center gap-3">
                  <div className="p-2 bg-gray-100 group-hover:bg-blue-500 rounded-lg transition-colors duration-200">
                    <Plus className="h-5 w-5 text-gray-600 group-hover:text-white" />
                  </div>
                  <span className="font-medium text-gray-700 group-hover:text-blue-600">
                    Create New Batch
                  </span>
                </div>
              </button>

              {/* Batch Cards */}
              {batches.map((batch, index) => (
                <div
                  key={batch.id}
                  className="bg-white rounded-xl border border-gray-200 shadow-sm"
                >
                  <div className="bg-gradient-to-r from-blue-50 to-purple-50 px-4 py-3 border-b border-gray-200 rounded-t-xl">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 flex-1">
                        <input
                          type="text"
                          value={batch.name}
                          onChange={(e) =>
                            handleNameChange(batch.id, e.target.value)
                          }
                          className="font-semibold text-gray-900 bg-transparent border-none outline-none focus:bg-white focus:border focus:border-blue-500 rounded px-2 py-1"
                        />
                        <span className="text-sm text-gray-600">
                          ({batch.products.length} items)
                        </span>
                      </div>
                      <button
                        onClick={() => handleDeleteBatch(batch.id)}
                        className="p-1 text-red-500 hover:bg-red-50 rounded transition-colors duration-200"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>

                    <div className="mt-3 flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-blue-600" />
                        <span className="text-sm font-medium text-gray-700">
                          Delivery Date: To be set by supplier
                        </span>
                      </div>

                      {/* <input
                        type="date"
                        value={batch.deliveryDate || ""}
                        onChange={(e) =>
                          handleDateChange(batch.id, e.target.value)
                        }
                        className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        min={
                          new Date(Date.now() + 2 * 24 * 60 * 60 * 1000)
                            .toISOString()
                            .split("T")[0]
                        }
                      /> */}
                    </div>
                  </div>

                  <div className="p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                      {batch.products.map((product) => (
                        <ProductCard
                          key={`${batch.id}-${product.productId}`}
                          product={product}
                          currentBatchId={batch.id}
                          inBatch={true}
                          onRemoveClick={(productId) =>
                            handleRemoveProductFromBatch(productId, batch.id)
                          }
                          activeDropdown={activeDropdown}
                          setActiveDropdown={setActiveDropdown}
                          batches={batches}
                          onMoveProductToBatch={handleMoveProductToBatch}
                          onUpdateProductQuantity={handleUpdateProductQuantity}
                        />
                      ))}
                    </div>

                    {batch.products.length === 0 && (
                      <div className="text-center py-8 text-gray-400 border-2 border-dashed border-gray-200 rounded-lg">
                        <Package className="h-6 w-6 mx-auto mb-2" />
                        <p className="text-sm">No products in this batch</p>
                        <p className="text-xs">
                          Add products from the unassigned section
                        </p>
                      </div>
                    )}

                    {batch.products.length > 0 && (
                      <div className="flex justify-between items-center pt-3 border-t border-gray-200">
                        <span className="font-medium text-gray-700">
                          Batch Total:
                        </span>
                        <span className="font-bold text-blue-600">
                          ₱{moneyFormatter(batch.totalCost, 2)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              ))}

              {batches.length === 0 && (
                <div className="text-center py-12 text-gray-500">
                  <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <h3 className="font-medium mb-2">No batches created yet</h3>
                  <p className="text-sm">
                    Create your first batch to start organizing products
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end items-center mt-8 pt-6 border-t border-gray-200">
          <div className="flex items-center gap-4">
            {/* {!isReadyToConfirm && (
              <div className="text-sm text-amber-600 bg-amber-50 px-3 py-2 rounded-lg border border-amber-200">
                {batches.length === 0 && "Create at least one batch"}
                {batches.some((b) => !b.deliveryDate) &&
                  "Set delivery dates for all batches"}
                {batches.length > 0 &&
                  !batches.some((b) =>
                    b.products.some((p) => p.batchQuantity > 0)
                  ) &&
                  "Add products with quantities to batches"}
              </div>
            )} */}

            <button
              onClick={onConfirm}
              disabled={!isReadyToConfirm}
              className={`px-8 py-3 rounded-xl font-semibold text-white transition-all duration-200 ${
                isReadyToConfirm
                  ? "bg-gradient-to-r from-blue-600 to-purple-700 hover:from-blue-700 hover:to-purple-800 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                  : "bg-gray-300 text-gray-500 cursor-not-allowed"
              }`}
            >
              Confirm Staggered Order
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
